/**
 * Milyarder yarışması ses motoru. Dosya kullanmaz; bütün sesler Web Audio ile üretilir.
 * Müzik ve efektler ayrı kanallardadır ve ayrı ayrı kapatılabilir. Özgün tasarımdır; hiçbir
 * TV programının müziği taklit edilmez.
 */
const MUSIC_KEY = 'odevlendir-billionaire-music-muted';
const FX_KEY = 'odevlendir-billionaire-fx-muted';
const LEGACY_KEY = 'odevlendir-billionaire-muted';

export const billionaireAudioMix = {
  master: 1,
  music: 0.38,
  fx: 1.15,
  reverbSend: 0.24,
  reverbSeconds: 2.6,
  reverbDecay: 3.2,
  /** Gerilim müziğinin temposu: ilk sorudan son soruya doğru artar (BPM). */
  bedTempoFrom: 84,
  bedTempoTo: 112,
} as const;

type VoiceOptions = {
  type?: OscillatorType; gain?: number; attack?: number; release?: number; detune?: number[];
  filter?: { type: BiquadFilterType; from: number; to?: number; q?: number };
  music?: boolean; reverb?: number; endFrequency?: number;
};
type NoiseOptions = { gain?: number; type?: BiquadFilterType; from?: number; to?: number; q?: number; attack?: number; music?: boolean; reverb?: number };

/** Orta A = 440 Hz; nota adı yerine yarım ses sayısı kullanılır. */
const hz = (semitonesFromA4: number) => 440 * Math.pow(2, semitonesFromA4 / 12);
/** Gerilim müziği akor dizisi: Am – F – C – G (kök yarım ses, A4'e göre). */
const BED_CHORDS = [
  { root: -24, notes: [-12, -9, -5] },
  { root: -28, notes: [-16, -12, -9] },
  { root: -21, notes: [-9, -5, -2] },
  { root: -26, notes: [-14, -10, -7] },
] as const;

function readFlag(key: string) { try { return window.localStorage.getItem(key) === 'true'; } catch { return false; } }
function writeFlag(key: string, value: boolean) { try { window.localStorage.setItem(key, String(value)); } catch { /* isteğe bağlı */ } }

export class BillionaireAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private fxBus: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private musicMuted = readFlag(MUSIC_KEY);
  private fxMuted = readFlag(FX_KEY);

  constructor() {
    // Eski tek düğmeli sürümün "sessiz" ayarı yalnız bir kez yeni ayarlara taşınır; sonra silinir.
    try {
      if (window.localStorage.getItem(LEGACY_KEY) !== null) {
        const legacy = readFlag(LEGACY_KEY);
        if (window.localStorage.getItem(MUSIC_KEY) === null) { this.musicMuted = legacy; writeFlag(MUSIC_KEY, legacy); }
        if (window.localStorage.getItem(FX_KEY) === null) { this.fxMuted = legacy; writeFlag(FX_KEY, legacy); }
        window.localStorage.removeItem(LEGACY_KEY);
      }
    } catch { /* isteğe bağlı */ }
  }
  private bed: { timer: number; step: number; nextTime: number; intensity: number; ducked: boolean } | null = null;
  private lastTick = 0;

  private ensure() {
    if (this.context && this.master) {
      if (this.context.state === 'suspended') void this.context.resume();
      return this.context;
    }
    const context = new AudioContext();
    const compressor = context.createDynamicsCompressor();
    // Sınırlayıcı: yükseltilmiş seviyede tepe noktaları bozulmadan tutulur.
    compressor.threshold.value = -10; compressor.knee.value = 6; compressor.ratio.value = 12; compressor.attack.value = 0.002; compressor.release.value = 0.2;
    const master = context.createGain(); master.gain.value = billionaireAudioMix.master;
    const musicBus = context.createGain(); musicBus.gain.value = this.musicMuted ? 0 : billionaireAudioMix.music;
    const fxBus = context.createGain(); fxBus.gain.value = this.fxMuted ? 0 : billionaireAudioMix.fx;
    const reverb = context.createConvolver(); reverb.buffer = this.createImpulse(context);
    const reverbReturn = context.createGain(); reverbReturn.gain.value = 0.9;
    musicBus.connect(master); fxBus.connect(master); reverb.connect(reverbReturn).connect(master);
    master.connect(compressor).connect(context.destination);
    Object.assign(this, { context, master, musicBus, fxBus, reverb });
    return context;
  }

  /** Stüdyo yankısı: üstel sönen stereo gürültüden üretilen dürtü yanıtı. */
  private createImpulse(context: AudioContext) {
    const length = Math.floor(context.sampleRate * billionaireAudioMix.reverbSeconds);
    const impulse = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < 2; channel += 1) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, billionaireAudioMix.reverbDecay);
    }
    return impulse;
  }

  private noiseSource(context: AudioContext) {
    if (!this.noiseBuffer) {
      const buffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    const source = context.createBufferSource(); source.buffer = this.noiseBuffer; source.loop = true;
    return source;
  }

  private route(node: AudioNode, music: boolean, reverbAmount: number) {
    node.connect(music ? this.musicBus! : this.fxBus!);
    if (reverbAmount > 0 && this.reverb && this.context) {
      const send = this.context.createGain(); send.gain.value = reverbAmount;
      node.connect(send).connect(this.reverb);
    }
  }

  /** Akortsuz katmanlı osilatör sesi; filtre zarfı ve yankı gönderimiyle. */
  private voice(frequency: number, start: number, duration: number, options: VoiceOptions = {}) {
    const context = this.ensure();
    const at = context.currentTime + start;
    const { type = 'sine', gain = 0.08, attack = 0.012, release = 0.25, detune = [0], filter, music = false, reverb = billionaireAudioMix.reverbSend } = options;
    const volume = context.createGain();
    volume.gain.setValueAtTime(0.0001, at);
    volume.gain.exponentialRampToValueAtTime(gain, at + attack);
    volume.gain.setValueAtTime(gain, at + Math.max(attack, duration - release));
    volume.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    let output: AudioNode = volume;
    if (filter) {
      const biquad = context.createBiquadFilter(); biquad.type = filter.type; biquad.Q.value = filter.q ?? 0.8;
      biquad.frequency.setValueAtTime(filter.from, at);
      if (filter.to) biquad.frequency.exponentialRampToValueAtTime(filter.to, at + duration * 0.8);
      volume.connect(biquad); output = biquad;
    }
    this.route(output, music, reverb);
    for (const cents of detune) {
      const oscillator = context.createOscillator(); oscillator.type = type; oscillator.detune.value = cents;
      oscillator.frequency.setValueAtTime(frequency, at);
      if (options.endFrequency) oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, options.endFrequency), at + duration);
      oscillator.connect(volume); oscillator.start(at); oscillator.stop(at + duration + 0.05);
    }
  }

  private noise(start: number, duration: number, options: NoiseOptions = {}) {
    const context = this.ensure();
    const at = context.currentTime + start;
    const { gain = 0.06, type = 'bandpass', from = 1200, to, q = 0.9, attack = 0.01, music = false, reverb = billionaireAudioMix.reverbSend } = options;
    const source = this.noiseSource(context);
    const filter = context.createBiquadFilter(); filter.type = type; filter.Q.value = q;
    filter.frequency.setValueAtTime(from, at);
    if (to) filter.frequency.exponentialRampToValueAtTime(to, at + duration);
    const volume = context.createGain();
    volume.gain.setValueAtTime(0.0001, at);
    volume.gain.exponentialRampToValueAtTime(gain, at + attack);
    volume.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter).connect(volume); this.route(volume, music, reverb);
    source.start(at, Math.random()); source.stop(at + duration + 0.05);
  }

  /** Bas vuruş + küçük hoparlörlerde duyulması için orta frekans gövdesi ve tıklama. */
  private kick(start: number, gain = 0.5, music = false) {
    this.voice(130, start, 0.32, { type: 'sine', gain, attack: 0.003, release: 0.28, endFrequency: 42, music, reverb: 0.05 });
    this.voice(240, start, 0.12, { type: 'triangle', gain: gain * 0.45, attack: 0.002, release: 0.1, endFrequency: 140, music, reverb: 0.05 });
    this.noise(start, 0.03, { type: 'highpass', from: 2500, gain: gain * 0.25, attack: 0.001, music, reverb: 0 });
  }

  private brass(notes: number[], start: number, duration: number, gain = 0.05) {
    notes.forEach((semitone) => this.voice(hz(semitone), start, duration, {
      type: 'sawtooth', gain, attack: 0.03, release: duration * 0.6, detune: [-7, 6],
      filter: { type: 'lowpass', from: 1500, to: 4500, q: 1.2 }, reverb: 0.3,
    }));
  }

  private bells(notes: number[], start: number, spacing: number, gain = 0.045) {
    notes.forEach((semitone, index) => {
      this.voice(hz(semitone), start + index * spacing, 0.9, { type: 'sine', gain, attack: 0.004, release: 0.85, reverb: 0.45 });
      this.voice(hz(semitone + 12), start + index * spacing, 0.5, { type: 'triangle', gain: gain * 0.35, attack: 0.004, release: 0.45, reverb: 0.45 });
    });
  }

  private crash(start: number, gain = 0.05, duration = 1.4) {
    this.noise(start, duration, { type: 'highpass', from: 5200, gain, attack: 0.003, reverb: 0.35 });
  }

  /** Kısa alkış: rastgele zamanlı süzülmüş gürültü taneleri. */
  private applause(start: number, duration: number, gain = 0.035) {
    const grains = Math.round(duration * 38);
    for (let i = 0; i < grains; i += 1) {
      const t = start + Math.random() * duration;
      const fade = 1 - (t - start) / duration;
      this.noise(t, 0.03 + Math.random() * 0.03, { type: 'bandpass', from: 1400 + Math.random() * 2600, q: 1.4, gain: gain * (0.4 + fade * 0.6), attack: 0.002, reverb: 0.3 });
    }
  }

  unlock() { this.ensure(); }
  isMusicMuted() { return this.musicMuted; }
  isFxMuted() { return this.fxMuted; }
  /** Geriye uyumluluk: ikisi birden kapalıysa "sessiz". */
  isMuted() { return this.musicMuted && this.fxMuted; }

  private applyLevels() {
    if (!this.context) return;
    const now = this.context.currentTime;
    this.musicBus?.gain.setTargetAtTime(this.musicMuted ? 0 : billionaireAudioMix.music * (this.bed?.ducked ? 0.25 : 1), now, 0.08);
    this.fxBus?.gain.setTargetAtTime(this.fxMuted ? 0 : billionaireAudioMix.fx, now, 0.03);
  }

  toggleMusic() { this.musicMuted = !this.musicMuted; writeFlag(MUSIC_KEY, this.musicMuted); this.ensure(); this.applyLevels(); return this.musicMuted; }
  toggleEffects() { this.fxMuted = !this.fxMuted; writeFlag(FX_KEY, this.fxMuted); this.ensure(); this.applyLevels(); return this.fxMuted; }
  /** M kısayolu: bir kanal açıksa hepsini kapatır, ikisi de kapalıysa hepsini açar. */
  toggleMuted() {
    const mute = !(this.musicMuted && this.fxMuted);
    this.musicMuted = mute; this.fxMuted = mute; writeFlag(MUSIC_KEY, mute); writeFlag(FX_KEY, mute);
    this.ensure(); this.applyLevels();
    return mute;
  }

  /** Soru boyunca çalan alçak sesli gerilim müziği; `progress` 0–1 para merdivenindeki yer. */
  startBed(progress: number) {
    const context = this.ensure();
    const intensity = Math.min(1, Math.max(0, progress));
    if (this.bed) { this.bed.intensity = intensity; this.bed.ducked = false; this.applyLevels(); return; }
    this.bed = { timer: 0, step: 0, nextTime: context.currentTime + 0.05, intensity, ducked: false };
    this.applyLevels();
    this.bed.timer = window.setInterval(() => this.scheduleBed(), 25);
  }

  private scheduleBed() {
    const bed = this.bed; const context = this.context;
    if (!bed || !context) return;
    const tempo = billionaireAudioMix.bedTempoFrom + (billionaireAudioMix.bedTempoTo - billionaireAudioMix.bedTempoFrom) * bed.intensity;
    const sixteenth = 60 / tempo / 4;
    while (bed.nextTime < context.currentTime + 0.12) {
      const offset = bed.nextTime - context.currentTime;
      const step = bed.step % 16; const chord = BED_CHORDS[Math.floor(bed.step / 32) % BED_CHORDS.length];
      if (step === 0 && bed.step % 32 === 0) chord.notes.forEach((note) => this.voice(hz(note), offset, sixteenth * 32, { type: 'triangle', gain: 0.03, attack: 0.6, release: 1.2, detune: [-6, 5], filter: { type: 'lowpass', from: 900 }, music: true, reverb: 0.4 }));
      if ([0, 6, 8, 14].includes(step)) this.voice(hz(chord.root + (step === 8 ? 12 : 0)), offset, sixteenth * 1.6, { type: 'sawtooth', gain: 0.05, attack: 0.006, release: 0.12, filter: { type: 'lowpass', from: 380 + bed.intensity * 300, q: 2 }, music: true, reverb: 0.1 });
      if (step % 4 === 2) this.noise(offset, 0.05, { type: 'highpass', from: 7000, gain: 0.012 + bed.intensity * 0.01, music: true, reverb: 0.05 });
      if (step === 0) this.kick(offset, 0.14 + bed.intensity * 0.08, true);
      bed.nextTime += sixteenth; bed.step += 1;
    }
  }

  /** Kilit anında müzik kısılır; kalp atışı öne çıkar. */
  duckBed(ducked: boolean) { if (this.bed) { this.bed.ducked = ducked; this.applyLevels(); } }

  stopBed() {
    if (!this.bed) return;
    window.clearInterval(this.bed.timer); this.bed = null; this.applyLevels();
  }

  intro() {
    this.noise(0, 1.3, { type: 'bandpass', from: 300, to: 5000, q: 1.2, gain: 0.05, attack: 1.1, reverb: 0.4 });
    [-12, -5, 0, 4].forEach((note) => this.voice(hz(note - 12), 0.2, 2.2, { type: 'triangle', gain: 0.035, attack: 0.9, release: 1, detune: [-6, 6], reverb: 0.5 }));
    this.kick(1.25, 0.6); this.brass([-12, -5, 0, 3], 1.25, 1.1, 0.045); this.crash(1.25, 0.05); this.bells([12, 15, 19, 24], 1.35, 0.08);
  }

  /** Logolu geçiş: süzülen rüzgâr, logonun parlaması ve vuruş (yaklaşık 1,4 sn). */
  transition() {
    this.noise(0, 0.75, { type: 'bandpass', from: 350, to: 3800, q: 1.6, gain: 0.06, attack: 0.55, reverb: 0.3 });
    this.kick(0.72, 0.45); this.brass([-17, -12, -8], 0.72, 0.55, 0.035); this.bells([19, 24, 28, 31], 0.8, 0.07, 0.035);
  }

  /** Cevap kilidi: turuncu kilit tınısı ve kalp atışı gibi iki çift boğuk vuruş. */
  lock() {
    this.duckBed(true);
    this.voice(hz(-5), 0, 0.2, { type: 'square', gain: 0.035, attack: 0.004, release: 0.15, filter: { type: 'lowpass', from: 1800 }, reverb: 0.2 });
    [0.12, 0.38, 0.72, 0.98].forEach((t, i) => {
      this.voice(62, t, 0.24, { type: 'sine', gain: i % 2 ? 0.28 : 0.42, attack: 0.004, release: 0.2, endFrequency: 40, reverb: 0.08 });
      this.voice(190, t, 0.14, { type: 'triangle', gain: i % 2 ? 0.1 : 0.15, attack: 0.003, release: 0.12, endFrequency: 120, reverb: 0.08 });
    });
    this.voice(hz(-9), 0, 1.2, { type: 'sawtooth', gain: 0.03, attack: 0.5, release: 0.3, detune: [-8, 8], filter: { type: 'lowpass', from: 600, to: 2600, q: 1.5 }, reverb: 0.4 });
  }

  correct() {
    this.duckBed(false);
    this.kick(0, 0.55); this.brass([-9, -5, -2, 3], 0, 0.9, 0.05); this.crash(0, 0.045);
    this.bells([15, 19, 22, 27], 0.12, 0.06); this.applause(0.15, 1.4, 0.03);
  }

  wrong() {
    this.duckBed(false);
    this.voice(hz(-19), 0, 0.9, { type: 'sawtooth', gain: 0.05, attack: 0.02, release: 0.6, detune: [-10, 10], endFrequency: hz(-24), filter: { type: 'lowpass', from: 1900, to: 450 }, reverb: 0.35 });
    this.voice(hz(-18), 0, 0.9, { type: 'sawtooth', gain: 0.035, attack: 0.02, release: 0.6, endFrequency: hz(-23), filter: { type: 'lowpass', from: 1700, to: 420 }, reverb: 0.35 });
    this.voice(hz(-7), 0, 0.7, { type: 'square', gain: 0.02, attack: 0.02, release: 0.5, endFrequency: hz(-12), filter: { type: 'lowpass', from: 2200, to: 700 }, reverb: 0.3 });
    this.kick(0, 0.5); this.noise(0.05, 0.7, { type: 'lowpass', from: 900, to: 120, gain: 0.05, reverb: 0.3 });
  }

  /** Kasa sayacı tıkırtısı; saniyede en fazla ~14 kez. */
  moneyTick() {
    if (!this.context) return;
    const now = performance.now(); if (now - this.lastTick < 70) return; this.lastTick = now;
    this.voice(hz(27 + Math.floor(Math.random() * 3)), 0, 0.09, { type: 'triangle', gain: 0.03, attack: 0.002, release: 0.08, reverb: 0.1 });
  }

  cardFlip() {
    this.noise(0, 0.08, { type: 'highpass', from: 2400, gain: 0.12, attack: 0.002, reverb: 0.1 });
    this.voice(hz(7), 0.02, 0.12, { type: 'triangle', gain: 0.05, attack: 0.003, release: 0.1 });
  }

  cardSlide() {
    this.noise(0, 0.42, { type: 'bandpass', from: 700, to: 2600, q: 1.4, gain: 0.22, attack: 0.12, reverb: 0.15 });
  }

  /** Eski arayüz uyumluluğu: karıştırma başlangıcı. */
  shuffle() { this.cardFlip(); }

  reveal(positive: boolean) {
    if (positive) { this.bells([12, 16, 19, 24, 28], 0, 0.07, 0.05); this.crash(0.05, 0.03, 1); return; }
    this.voice(hz(-14), 0, 0.7, { type: 'sawtooth', gain: 0.04, attack: 0.02, release: 0.5, endFrequency: hz(-20), filter: { type: 'lowpass', from: 1200, to: 300 }, reverb: 0.3 });
  }

  finish() {
    this.stopBed();
    const progression = [[-12, -8, -5, 0], [-7, -3, 0, 5], [-5, -1, 2, 7], [-12, -8, -5, 0, 4]];
    progression.forEach((chord, index) => { this.kick(index * 0.42, 0.5); this.brass(chord, index * 0.42, index === 3 ? 1.8 : 0.45, 0.045); });
    this.crash(1.26, 0.06, 2.2); this.bells([24, 28, 31, 36], 1.3, 0.09); this.applause(0.9, 3, 0.04);
  }

  dispose() {
    this.stopBed();
    void this.context?.close();
    this.context = null; this.master = null; this.musicBus = null; this.fxBus = null; this.reverb = null;
  }
}
